import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Skeleton, Button, Card, CardContent } from '@mui/material';
import BuyerDetailsModal from './BuyerDetailsModal';
import dayjs from 'dayjs';
import { IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeSelector from './DateRangeSelector';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const AdminDashboard = ({ dateRange, onDateRangeChange, userId }) => {
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [dateRangee, setSelectedData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { startDate, endDate } = dateRange || {};
  const [isDateRangeSelectorOpen, setDateRangeSelectorOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeChart, setActiveChart] = useState('ROI');
  const [sortField, setSortField] = useState('nameBuyer');
  const [sortDirection, setSortDirection] = useState('asc');

  const parseProfit = (profitStr) => {
    if (typeof profitStr === 'number') return profitStr;
    if (!profitStr) return 0;
    const cleaned = profitStr.replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const parseNumeric = (val) => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const cleaned = String(val).replace(/[$,%]/g, '');
    return parseFloat(cleaned) || 0;
  };

  const getRowColor = (profit) => {
    const profitValue = parseProfit(profit);
    if (profitValue > 0) return '#e8f5e9';
    if (profitValue < 0) return '#ffebee';
    return 'white';
  };

  const getHoverColor = (profit) => {
    const profitValue = parseProfit(profit);
    if (profitValue > 0) return '#c8e6c9';
    if (profitValue < 0) return '#ffcdd2';
    return '#f5f5f5';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedBuyers = () => {
    return [...buyers].sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'nameBuyer':
          aVal = a.nameBuyer || '';
          bVal = b.nameBuyer || '';
          break;
        case 'totalIncome':
          aVal = parseNumeric(a.totalIncome);
          bVal = parseNumeric(b.totalIncome);
          break;
        case 'expensesAgn':
          aVal = parseNumeric(a.expensesAgn);
          bVal = parseNumeric(b.expensesAgn);
          break;
        case 'expensesAcc':
          aVal = parseNumeric(a.expensesAcc);
          bVal = parseNumeric(b.expensesAcc);
          break;
        case 'profit':
          aVal = parseProfit(a.profit);
          bVal = parseProfit(b.profit);
          break;
        case 'Roi':
          aVal = parseNumeric(a.Roi);
          bVal = parseNumeric(b.Roi);
          break;
        case 'totalFirstdeps':
          aVal = parseNumeric(a.totalFirstdeps);
          bVal = parseNumeric(b.totalFirstdeps);
          break;
        default:
          aVal = a[sortField];
          bVal = b[sortField];
      }

      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const headerStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    cursor: 'pointer',
    userSelect: 'none',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#e3f2fd',
    },
  };

  const handleDateRangeSelected = (range) => {
    onDateRangeChange(range);
    setDateRangeSelectorOpen(false);
  };

  useEffect(() => {
    const fetchBuyers = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/api/admin/buyers',
          {
            params: {
              startDate: dayjs(startDate).format('YYYY-MM-DD'),
              endDate: dayjs(endDate).format('YYYY-MM-DD')
            }
          }
        );

        setTimeout(() => {
          setBuyers(response.data);
          setLoading(false);
        }, 900);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchBuyers();
    }
  }, [startDate, endDate])

  const handleRowClick = async (buyer) => {
    try {
      const response = await axios.get(`/api/buyer/${buyer.nameBuyer}/records`,
        {
          params: {
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            userId: userId
          }
        });

      const buyerDetails = {
        nameBuyer: buyer.nameBuyer,
        records: response.data,
      };
      const dateRangeBuyer = {
        startDate: dayjs(startDate).format('YYYY-MM-DD'),
        endDate: dayjs(endDate).format('YYYY-MM-DD')
      }

      setSelectedData(dateRangeBuyer)
      setSelectedBuyer(buyerDetails);
      setModalOpen(true);
    } catch (error) {
      console.error('Error getting buyer data:', error);
    }
  };

  const savedReject = async (event, buyerId) => {
    try {
      const newValue = event.target.value;
      await axios.put(`/api/buyers/${buyerId}`, {
        reject: newValue,
      });
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const chartDataROI = buyers.map(buyer => ({
    name: buyer.nameBuyer,
    ROI: buyer.Roi || 0
  }));

  const chartDataProfit = buyers.map(buyer => ({
    name: buyer.nameBuyer,
    Profit: parseProfit(buyer.profit)
  }));

  const tabStyle = (isActive) => ({
    minWidth: 80,
    px: 2,
    py: 0.5,
    mr: 1,
    borderRadius: 1,
    textTransform: 'none',
    fontWeight: isActive ? 600 : 400,
    backgroundColor: isActive ? '#1976d2' : '#e0e0e0',
    color: isActive ? '#fff' : '#333',
    '&:hover': {
      backgroundColor: isActive ? '#1565c0' : '#bdbdbd',
    },
  });

  const sortedBuyers = getSortedBuyers();

  // Calculate totals
  const totals = buyers.reduce((acc, buyer) => {
    acc.revenue += parseNumeric(buyer.totalIncome);
    acc.expensesAgn += parseNumeric(buyer.expensesAgn);
    acc.expensesAcc += parseNumeric(buyer.expensesAcc);
    acc.profit += parseProfit(buyer.profit);
    acc.fd += parseNumeric(buyer.totalFirstdeps);
    acc.reject += parseNumeric(buyer.reject);
    return acc;
  }, { revenue: 0, expensesAgn: 0, expensesAcc: 0, profit: 0, fd: 0, reject: 0 });

  const totalSpent = totals.expensesAgn + totals.expensesAcc;
  const totalRoi = totalSpent > 0 ? Math.round((totals.profit / totalSpent) * 100) : 0;


  return (
    <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', width: '80%', justifyContent: 'center' }}>
        {loading ? (
          <>
             <Box sx={{ marginTop: 16, padding: 3, borderRadius: 3, boxShadow: 5 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: -1.5 }}>
            <Skeleton variant="rectangular" width="40%" animation="wave"  height={30} sx={{ marginBottom: 1, borderRadius:2 }}></Skeleton>
            <Skeleton variant="circular" width="40px" animation="wave"  height={"40px"} sx={{ marginBottom: 1,marginLeft:1,  }}/>
        </Box>
        <Skeleton variant="rectangular" width="20%" animation="wave"  height={15} sx={{ marginTop: 2,marginBottom: 2, borderRadius:1 }}></Skeleton>

        <DateRangeSelector
          open={isDateRangeSelectorOpen}
          onClose={() => setDateRangeSelectorOpen(false)}
          onRangeSelected={handleDateRangeSelected}
        />
            <Box sx={{ minWidth: 650, mt:1}} aria-label="buyers table">
              <Skeleton variant="rectangular" width="100%" animation="wave"  height={30} sx={{ marginBottom: 1, borderRadius:2 }} />
              <Skeleton variant="rectangular" width="80%" animation="wave" height={30} sx={{ marginBottom: 1, borderRadius:2}} />
              <Skeleton variant="rectangular" width="60%" animation="wave" height={30} sx={{ marginBottom: 1, borderRadius:2}} />
              <Skeleton variant="rectangular" width="100%" animation="wave" height={30} sx={{ marginBottom: 1, borderRadius:2 }} />
              <Skeleton variant="rectangular" width="80%" animation="wave" height={30} sx={{ marginBottom: 1, borderRadius:2}} />
              <Skeleton variant="rectangular" width="90%" animation="wave" height={30} sx={{ marginBottom: 1, borderRadius:2}} />
            </Box>
            </Box>
          </>
        ) : (
      <Box sx={{ marginTop: 16, padding: 3, borderRadius: 3, boxShadow: 5 }}>

        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: -1.5 }}>

          <Typography variant="h5" gutterBottom >
            Панель администратора
          </Typography>
          <IconButton
            color="primary"
            onClick={() => setDateRangeSelectorOpen(true)}
            sx={{
              padding: '8px'
            }}
          >
            <CalendarTodayIcon />
          </IconButton>
        </Box>
        <DateRangeSelector
          open={isDateRangeSelectorOpen}
          onClose={() => setDateRangeSelectorOpen(false)}
          onRangeSelected={handleDateRangeSelected}
        />

        {buyers.length > 0 && (
          <Box sx={{ width: '100%', height: 280, mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', mb: 1 }}>
              <Button
                size="small"
                onClick={() => setActiveChart('ROI')}
                sx={tabStyle(activeChart === 'ROI')}
              >
                ROI
              </Button>
              <Button
                size="small"
                onClick={() => setActiveChart('Profit')}
                sx={tabStyle(activeChart === 'Profit')}
              >
                Profit
              </Button>
            </Box>
            <ResponsiveContainer width="100%" height="90%">
              {activeChart === 'ROI' ? (
                <BarChart data={chartDataROI} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} unit="%" />
                  <Tooltip formatter={(value) => [`${value}%`, 'ROI']} />
                  <Bar dataKey="ROI" radius={[4, 4, 0, 0]}>
                    {chartDataROI.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.ROI < 0 ? '#f44336' : entry.ROI < 30 ? '#ffeb3b' : '#4caf50'} />
                    ))}
                  </Bar>
                </BarChart>
              ) : (
                <BarChart data={chartDataProfit} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 12 }} unit="$" />
                  <Tooltip formatter={(value) => [`$${value}`, 'Profit']} />
                  <Bar dataKey="Profit" radius={[4, 4, 0, 0]}>
                    {chartDataProfit.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.Profit >= 0 ? '#4caf50' : '#f44336'} />
                    ))}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </Box>
        )}

        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Card sx={{ minWidth: 120, boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary">Revenue</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                ${totals.revenue}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 120, boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary">Spent</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                ${totalSpent}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 120, boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary">Profit</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: totals.profit >= 0 ? '#4caf50' : '#f44336' }}>
                ${totals.profit}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 120, boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary">ROI</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                {totalRoi}%
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ minWidth: 120, boxShadow: 3, borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="textSecondary">FD</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                {totals.fd}
              </Typography>
            </CardContent>
          </Card>
        </Box>

          <TableContainer component={Paper} sx={{ boxShadow: 0, borderRadius: 0 }}  >
            <Typography align="left" sx={{ padding: 1, fontSize: 15, borderBottom: 'none', verticalAlign: 'sub', width: '40%' }}>{dayjs(startDate).format('YYYY.MM.DD')} — {dayjs(endDate).format('YYYY.MM.DD')}</Typography>

            <Table sx={{ minWidth: 650 }} aria-label="buyers table">
              <TableHead >
                <TableRow  >
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('nameBuyer')}>
                    Name{getSortArrow('nameBuyer')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('totalIncome')}>
                    Revenue{getSortArrow('totalIncome')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('expensesAgn')}>
                    Spent Agn{getSortArrow('expensesAgn')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('expensesAcc')}>
                    Spent Acc{getSortArrow('expensesAcc')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('profit')}>
                    Profit{getSortArrow('profit')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('Roi')}>
                    ROI{getSortArrow('Roi')}
                  </TableCell>
                  <TableCell align="left" sx={headerStyle} onClick={() => handleSort('totalFirstdeps')}>
                    FD{getSortArrow('totalFirstdeps')}
                  </TableCell>
                  <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)', width: '15%' }}>Reject</TableCell>
                </TableRow>
              </TableHead>
              <TableBody >
                {sortedBuyers.map((buyer) => (
                  <TableRow key={buyer.id}
                    onClick={(event) => {
                      event.currentTarget.blur();
                      handleRowClick(buyer);
                    }}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: getRowColor(buyer.profit),
                      '&:hover': {
                        backgroundColor: getHoverColor(buyer.profit),
                        transition: 'background-color 0.3s ease',
                      },
                      '&:focus': {
                        outline: 'none',
                      },
                    }}
                  >
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{buyer.nameBuyer}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{buyer.totalIncome}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{'$' + buyer.expensesAgn}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{'$' + buyer.expensesAcc}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{buyer.profit}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{buyer.Roi + '%'}</TableCell>
                    <TableCell align="left" sx={{ border: '1px solid rgba(224, 224, 224, 1)' }}>{buyer.totalFirstdeps}</TableCell>
                    <TableCell align="center" sx={{ border: '1px solid rgba(224, 224, 224, 1)', padding: '0px' }}>
                      <TextField size="small" defaultValue={buyer.reject} onClick={(event) => event.stopPropagation()} sx={{ width: '80%', margin: '-10px', padding: '1px' }}
                        onChange={(event) => savedReject(event, buyer.id)}></TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

      </Box>
      )}
      {selectedBuyer && dateRangee && (
        <BuyerDetailsModal open={modalOpen} handleClose={handleCloseModal} buyerDetails={selectedBuyer} dateRangeBuyer={dateRangee} />
      )}
    </Container>
  );
};

export default AdminDashboard;
